<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { patientDocumentsApi, type PatientDocument, type PatientDocumentForm } from '@/api/patient-documents';
import * as yup from 'yup';
import type { AxiosError } from 'axios';

const ONE_KB = 1024;
const ONE_MB = ONE_KB * ONE_KB;
const MAX_MB = 30;
const MAX_FILE_SIZE = MAX_MB * ONE_MB;

const { t } = useI18n();

interface Props {
  patientDocument?: PatientDocument | undefined;
  patientId: number;
  mode: 'new' | 'edit';
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const validationSchema = yup.object({
  name: yup.string().required(t('patientDocumentForm.fieldRequired')),
  document: yup.mixed().when([], {
    is: () => props.mode === 'new',
    then: (schema) => schema.required(t('patientDocumentForm.fieldRequired')).test(
      'is-valid-size',
      t('patientDocumentForm.fileSizeLimit'),
      (value) => value && ((value as FileList)[0]?.size ?? 0) <= MAX_FILE_SIZE),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const initialValues = {
  patientId: props.patientId,
  name: props.patientDocument?.name,
};

const { handleSubmit, errors, defineField } = useForm<PatientDocumentForm>({ validationSchema, initialValues });

const [name, nameAttrs] = defineField('name');
const [document, documentAttrs] = defineField('document');

const valuesAsFormData = computed(() => {
  const formData = new FormData();
  formData.append('patient_document[patient_id]', props.patientId.toString());
  formData.append('patient_document[name]', name.value);
  formData.append('patient_document[document]', document.value instanceof File ? document.value : document.value[0]);
  if (props.patientDocument?.id) {
    formData.append('patient_document[id]', props.patientDocument.id.toString());
  }

  return formData;
});

const queryClient = useQueryClient();

const { mutate: createPatientDocument, isError: isCreateError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => patientDocumentsApi.create(valuesAsFormData.value),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['patientDocuments', props.patientId] });
      emit('close');
    },
  },
);

const { mutate: updatePatientDocument, isError: isUpdateError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => patientDocumentsApi.update({ id: props.patientDocument?.id, name: name.value }),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['patientDocuments', props.patientId] });
      emit('close');
    },
  },
);

const submitButtonDisabled = ref(false);
const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  if (props.mode === 'edit') {
    updatePatientDocument();
  } else {
    createPatientDocument();
  }
});
</script>
<template>
  <v-card class="px-4 pb-4">
    <v-card-title class="mb-4 align-baseline d-flex">
      {{ mode === 'new' ? t('patientDocumentForm.createTitle') : t('patientDocumentForm.updateTitle') }}
      <v-spacer />
      <v-btn
        icon="mdi-window-close"
        variant="plain"
        @click="emit('close')"
      />
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-text-field
        v-model="name"
        v-bind="nameAttrs"
        variant="outlined"
        class="px-2"
        :label="t('patientDocument.name')"
        :error-messages="errors.name"
      />
      <v-file-input
        v-if="mode === 'new'"
        v-model="document"
        v-bind="documentAttrs"
        class="px-2"
        variant="outlined"
        :label="t('patientDocument.document')"
        :error-messages="errors.document"
      />
      <v-row>
        <v-spacer />
        <v-btn
          color="primary"
          type="submit"
          class="mb-4 mr-4"
          :disabled="submitButtonDisabled"
        >
          {{ mode === 'new' ? t('patientDocumentForm.createSubmit') : t('patientDocumentForm.updateSubmit') }}
        </v-btn>
      </v-row>
    </v-form>
    <v-alert
      v-if="isCreateError || isUpdateError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ t('userSession.defaultError') }}
    </v-alert>
  </v-card>
</template>
